// LectureQuestionSlideUp.jsx - MediaRecorder + Node STT + 세련된 탭 & 내역 디자인 (iOS/Safari 대응 포함)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from "react-i18next";
import languageMap from "../components/languageMap";
import nicknameMap from "../components/nicknameMap";
import { ReactComponent as RefreshIconLight } from '../assets/icons/svg/lecture/light/refresh.svg';
import { ReactComponent as RefreshIconDark } from '../assets/icons/svg/lecture/dark/refresh.svg';
import { ReactComponent as ChevronDownIconLight } from '../assets/icons/svg/lecture/light/chevron_down.svg';
import { ReactComponent as ChevronDownIconDark } from '../assets/icons/svg/lecture/dark/chevron_down.svg';
import { ReactComponent as CheckIcon } from '../assets/icons/svg/lecture/check.svg';
import { ReactComponent as MicIconLight } from '../assets/icons/svg/lecture/light/mic.svg';
import { ReactComponent as MicIconDark } from '../assets/icons/svg/lecture/dark/mic.svg';

const MAX_QUESTION_LENGTH = 200;
const MAX_NICKNAME_LENGTH = 20;
const MAX_RECORDING_MS = 60 * 1000;

const getMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return undefined;
    const types = ['audio/webm', 'audio/mp4', 'audio/ogg', 'audio/wav'];
    for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return undefined;
};

const generateRandomNickname = () => {
    const lang = localStorage.getItem('language') || 'ko';
    const isKo = lang === 'ko';
    const adj = nicknameMap.adjectives[Math.floor(Math.random() * nicknameMap.adjectives.length)];
    const noun = nicknameMap.nouns[Math.floor(Math.random() * nicknameMap.nouns.length)];
    const edu = nicknameMap.edu[Math.floor(Math.random() * nicknameMap.edu.length)];
    const name = isKo ? `${adj.ko} ${noun.ko}` : `${adj.en} ${noun.en}`;
    return { name, animal: edu };
};

const LectureQuestionSlideUp = ({
                                    show,
                                    onClose,
                                    filterKey,
                                    baseUrl,
                                    questionApiUrl,
                                    isDarkMode,
                                    viewingLanguage
                                }) => {
    const { t } = useTranslation();

    const [questionText, setQuestionText] = useState('');
    const [status, setStatus] = useState('idle');
    const [voiceStatus, setVoiceStatus] = useState('idle');
    const [selectedQuestionLang, setSelectedQuestionLang] = useState((viewingLanguage || 'ko').split('-')[0].toLowerCase());
    const [showLangSelector, setShowLangSelector] = useState(false);
    const [isVoiceSupported, setIsVoiceSupported] = useState(false);
    const [{ name: initName, animal: initAnimal }] = useState(() => generateRandomNickname());
    const [nickname, setNickname] = useState(initName);
    const [nicknameAnimal, setNicknameAnimal] = useState(initAnimal);
    const [isEditingNickname, setIsEditingNickname] = useState(false);
    const [activeTab, setActiveTab] = useState('question');
    const [questionHistory, setQuestionHistory] = useState([]);

    const textareaRef = useRef(null);
    const nicknameInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const recordingTimerRef = useRef(null);
    const voiceSubmissionHandled = useRef(false);

    const panelRef = useRef(null);
    const [panelHeight, setPanelHeight] = useState(0);
    const dragStartY = useRef(null);
    const dragDeltaY = useRef(0);

    useEffect(() => {
        if (!show) {
            setPanelHeight(0);
            return;
        }
        if (panelRef.current) {
            panelRef.current.style.transform = '';
            panelRef.current.style.transition = '';
            if (!panelHeight) {
                setPanelHeight(panelRef.current.offsetHeight);
            }
        }
    }, [show, panelHeight]);

    const tokenKey = 'lecturePageUserToken';
    const historyKey = `questionHistory_${filterKey}`;

    const normalizeLanguageCode = (code) => (code || 'ko').split('-')[0].toLowerCase();

    const getLanguageDisplayName = (langCode) => languageMap[langCode] || languageMap[normalizeLanguageCode(langCode)] || langCode;

    const clearRecordingTimer = useCallback(() => {
        if (recordingTimerRef.current) {
            clearTimeout(recordingTimerRef.current);
            recordingTimerRef.current = null;
        }
    }, []);

    const stopMediaStream = useCallback(() => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(t => t.stop());
            mediaStreamRef.current = null;
        }
    }, []);

    const baseLanguageOptions = [
        { code: 'en', actualCode: 'en', name: t('question.language.english'), description: t('question.language.englishDescription') },
        { code: 'ko', actualCode: 'ko', name: t('question.language.korean'), description: t('question.language.koreanDescription') }
    ];

    const questionLanguageOptions = (() => {
        const lang = normalizeLanguageCode(viewingLanguage);
        if (lang === 'ko' || lang === 'en') return baseLanguageOptions;
        return [
            { code: lang, actualCode: viewingLanguage, name: getLanguageDisplayName(lang), description: '' },
            ...baseLanguageOptions
        ];
    })();

    const getCurrentQuestionLanguage = useCallback(() => {
        const option = questionLanguageOptions.find(opt => opt.code === selectedQuestionLang);
        if (option) return { ...option, actualLangCode: option.actualCode };
        return { code: selectedQuestionLang, name: getLanguageDisplayName(selectedQuestionLang), actualLangCode: viewingLanguage || selectedQuestionLang };
    // getLanguageDisplayName은 static languageMap만 읽는 함수라 매 렌더마다 재생성되어도 동작 동일
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedQuestionLang, viewingLanguage, questionLanguageOptions]);

    useEffect(() => {
        if (show && filterKey) {
            const allKeys = Object.keys(localStorage);
            allKeys.forEach(key => {
                if (key.startsWith('questionHistory_') && key !== historyKey) {
                    localStorage.removeItem(key);
                }
            });
            const savedHistory = localStorage.getItem(historyKey);
            if (savedHistory) {
                try {
                    setQuestionHistory(JSON.parse(savedHistory));
                } catch {
                    setQuestionHistory([]);
                }
            }
        }
    }, [show, filterKey, historyKey]);

    const saveQuestionToHistory = useCallback((questionData) => {
        const newHistory = [questionData, ...questionHistory].slice(0, 50);
        setQuestionHistory(newHistory);
        localStorage.setItem(historyKey, JSON.stringify(newHistory));
    }, [questionHistory, historyKey]);

    useEffect(() => {
        if (
            typeof window !== 'undefined' &&
            typeof MediaRecorder !== 'undefined' &&
            'MediaRecorder' in window &&
            navigator.mediaDevices &&
            typeof navigator.mediaDevices.getUserMedia === 'function'
        ) {
            const mime = getMimeType();
            if (mime) { setIsVoiceSupported(true); return; }
        }
        setIsVoiceSupported(false);
    }, []);

    useEffect(() => {
        setSelectedQuestionLang(normalizeLanguageCode(viewingLanguage));
    }, [viewingLanguage]);

    const handleLanguageSelect = (langCode) => {
        setSelectedQuestionLang(langCode);
        setShowLangSelector(false);
    };

    useEffect(() => {
        if (show && textareaRef.current && activeTab === 'question') {
            const timer = setTimeout(() => textareaRef.current?.focus(), 350);
            document.body.style.overflow = 'hidden';
            return () => { clearTimeout(timer); document.body.style.overflow = ''; };
        } else if (show) {
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = ''; };
        } else {
            document.body.style.overflow = '';
        }
    }, [show, activeTab]);

    useEffect(() => {
        if (isEditingNickname) {
            const timer = setTimeout(() => {
                nicknameInputRef.current?.focus();
                nicknameInputRef.current?.select();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [isEditingNickname]);

    useEffect(() => {
        if (!show) {
            setQuestionText('');
            setStatus('idle');
            setVoiceStatus('idle');
            setShowLangSelector(false);
            setActiveTab('question');
            setIsEditingNickname(false);
            voiceSubmissionHandled.current = false;
            clearRecordingTimer();
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                try { mediaRecorderRef.current.stop(); } catch {}
            }
            stopMediaStream();
            mediaRecorderRef.current = null;
        }
    }, [show, clearRecordingTimer, stopMediaStream]);

    const handleNicknameChange = (e) => {
        setNickname(e.target.value.slice(0, MAX_NICKNAME_LENGTH));
    };
    const handleNicknameEditOpen = () => { setIsEditingNickname(true); setShowLangSelector(false); };
    const handleNicknameEditClose = () => {
        if (!nickname.trim()) {
            const { name, animal } = generateRandomNickname();
            setNickname(name);
            setNicknameAnimal(animal);
        }
        setIsEditingNickname(false);
    };
    const handleNicknameKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Escape') {
            e.preventDefault();
            handleNicknameEditClose();
        }
    };

    const handleVoiceSubmit = useCallback(async (voiceText) => {
        const trimmed = voiceText.trim();
        if (!trimmed || status === 'loading') return;
        setStatus('loading');
        try {
            let token = localStorage.getItem(tokenKey);
            if (!token) { token = crypto.randomUUID(); localStorage.setItem(tokenKey, token); }
            const currentLang = getCurrentQuestionLanguage();
            const sessionToken = sessionStorage.getItem('lectureSessionToken') || '';
            const res = await fetch(`${questionApiUrl}/api/question`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Token': sessionToken,
                },
                body: JSON.stringify({
                    filterKey,
                    studentToken: token,
                    studentId: token,
                    text: trimmed,
                    language: currentLang.actualLangCode,
                    tp: 'wav',
                    nickname: nickname.trim() || undefined
                }),
            });
            let data = {};
            try { data = await res.json(); } catch {}
            if (res.ok && data?.dropped) { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); return; }
            if (!res.ok) { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); return; }
            saveQuestionToHistory({
                id: Date.now() + Math.random(),
                text: trimmed,
                timestamp: new Date().toISOString(),
                language: currentLang.actualLangCode,
                type: 'voice',
                status: 'sent',
                nickname: nickname.trim() || ''
            });
            setQuestionText('');
            setStatus('success');
            setTimeout(() => { setStatus('idle'); onClose(); }, 600);
        } catch {
            setStatus('error');
            setTimeout(() => setStatus('idle'), 2000);
        }
    }, [filterKey, questionApiUrl, onClose, getCurrentQuestionLanguage, status, saveQuestionToHistory, nickname]);

    const startVoiceRecognition = useCallback(async () => {
        if (voiceStatus === 'listening' || voiceStatus === 'processing') return;
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setVoiceStatus('error'); setTimeout(() => setVoiceStatus('idle'), 2000); return;
        }
        try {
            setQuestionText('');
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            const mimeType = getMimeType();
            if (!mimeType) { stopMediaStream(); setVoiceStatus('error'); setTimeout(() => setVoiceStatus('idle'), 2000); return; }
            const mediaRecorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = mediaRecorder;
            const chunks = [];
            voiceSubmissionHandled.current = false;
            setVoiceStatus('listening');
            mediaRecorder.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
            mediaRecorder.onstop = async () => {
                clearRecordingTimer();
                try {
                    setVoiceStatus('processing');
                    const blob = new Blob(chunks, { type: mimeType });
                    const currentLang = getCurrentQuestionLanguage();
                    const formData = new FormData();
                    const ext = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : mimeType.includes('wav') ? 'wav' : 'webm';
                    formData.append('file', blob, `question.${ext}`);
                    formData.append('language', currentLang.actualLangCode);
                    formData.append('filterKey', filterKey);
                    const sessionToken = sessionStorage.getItem('lectureSessionToken') || '';
                    const res = await fetch(`${baseUrl}/api/stt`, {
                        method: "POST",
                        headers: { 'X-Session-Token': sessionToken },
                        body: formData,
                    });
                    if (!res.ok) { setVoiceStatus('error'); setTimeout(() => setVoiceStatus('idle'), 2000); return; }
                    const data = await res.json();
                    const transcript = (data?.transcript || '').trim();
                    if (!transcript) { setVoiceStatus('error'); setTimeout(() => setVoiceStatus('idle'), 2000); return; }
                    const clippedText = transcript.slice(0, MAX_QUESTION_LENGTH);
                    setQuestionText(clippedText);
                    if (!voiceSubmissionHandled.current) {
                        voiceSubmissionHandled.current = true;
                        await handleVoiceSubmit(clippedText);
                    }
                    setVoiceStatus('idle');
                } catch {
                    setVoiceStatus('error');
                    setTimeout(() => setVoiceStatus('idle'), 2000);
                } finally {
                    stopMediaStream();
                    mediaRecorderRef.current = null;
                }
            };
            mediaRecorder.start();
            recordingTimerRef.current = setTimeout(() => {
                if (mediaRecorderRef.current?.state === 'recording') {
                    try { mediaRecorderRef.current.stop(); } catch {}
                }
            }, MAX_RECORDING_MS);
        } catch {
            clearRecordingTimer();
            stopMediaStream();
            setVoiceStatus('error');
            setTimeout(() => setVoiceStatus('idle'), 2000);
        }
    }, [voiceStatus, baseUrl, filterKey, getCurrentQuestionLanguage, handleVoiceSubmit, clearRecordingTimer, stopMediaStream]);

    const stopVoiceRecognition = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            try { mediaRecorderRef.current.stop(); } catch { setVoiceStatus('idle'); }
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        const trimmed = questionText.trim();
        if (!trimmed || status === 'loading') return;
        setStatus('loading');
        try {
            let token = localStorage.getItem(tokenKey);
            if (!token) { token = crypto.randomUUID(); localStorage.setItem(tokenKey, token); }
            const currentLang = getCurrentQuestionLanguage();
            const sessionToken = sessionStorage.getItem('lectureSessionToken') || '';
            const res = await fetch(`${questionApiUrl}/api/question`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Session-Token': sessionToken,
                },
                body: JSON.stringify({
                    filterKey,
                    studentToken: token,
                    studentId: token,
                    text: trimmed,
                    language: currentLang.actualLangCode,
                    tp: 'txt',
                    nickname: nickname.trim() || undefined
                }),
            });
            let data = {};
            try { data = await res.json(); } catch {}
            if (res.ok && data?.dropped) { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); return; }
            if (!res.ok) { setStatus('error'); setTimeout(() => setStatus('idle'), 2000); return; }
            saveQuestionToHistory({
                id: Date.now() + Math.random(),
                text: trimmed,
                timestamp: new Date().toISOString(),
                language: currentLang.actualLangCode,
                type: 'text',
                status: 'sent',
                nickname: nickname.trim() || ''
            });
            setQuestionText('');
            setStatus('success');
            setTimeout(() => { setStatus('idle'); onClose(); }, 600);
        } catch {
            setStatus('error');
            setTimeout(() => setStatus('idle'), 2000);
        }
    }, [questionText, status, filterKey, questionApiUrl, onClose, getCurrentQuestionLanguage, saveQuestionToHistory, nickname]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
        if (e.key === 'Escape') onClose();
    };

    const handleDragStart = (e) => {
        dragStartY.current = e.touches[0].clientY;
        dragDeltaY.current = 0;
        if (panelRef.current) panelRef.current.style.transition = 'none';
    };

    const handleDragMove = (e) => {
        if (dragStartY.current === null) return;
        const delta = e.touches[0].clientY - dragStartY.current;
        dragDeltaY.current = Math.max(0, delta);
        if (panelRef.current) panelRef.current.style.transform = `translateY(${dragDeltaY.current}px)`;
    };

    const handleDragEnd = () => {
        if (dragStartY.current === null) return;
        if (panelRef.current) panelRef.current.style.transition = '';
        if (dragDeltaY.current > 80) {
            if (panelRef.current) panelRef.current.style.transform = 'translateY(100%)';
            onClose();
        } else if (panelRef.current) {
            panelRef.current.style.transform = '';
        }
        dragStartY.current = null;
        dragDeltaY.current = 0;
    };

    const formatTime = (timestamp) => new Date(timestamp).toLocaleTimeString('ko-KR', {
        hour: '2-digit', minute: '2-digit', hour12: false
    });

    const isLoading = status === 'loading';
    const isSuccess = status === 'success';
    const isError = status === 'error';
    const canSubmit = questionText.trim() && !isLoading && voiceStatus !== 'listening' && voiceStatus !== 'processing';
    const currentLang = getCurrentQuestionLanguage();

    return (
        <>
            {/* 딤드 백드롭 */}
            <div
                className={`fixed inset-0 z-[25] bg-black transition-opacity duration-300 ${
                    show ? 'bg-opacity-30' : 'bg-opacity-0 pointer-events-none'
                }`}
                onClick={onClose}
            />
            <div
                ref={panelRef}
                className={`fixed left-0 right-0 z-[26] bottom-[calc(env(safe-area-inset-bottom,0px)+4rem)] transition-transform duration-300 ease-out rounded-t-[20px] max-h-[70vh] flex flex-col overflow-hidden ${
                show ? 'translate-y-0' : 'translate-y-full'
            } ${isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'}`}
                style={panelHeight ? { height: panelHeight } : undefined}
>

                {/* 핸들 바 (드래그로 닫기) */}
                <div
                    className="flex justify-center py-2 cursor-grab touch-none"
                    onTouchStart={handleDragStart}
                    onTouchMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                >
                    <div className={`w-10 h-1 rounded-[2.5px] ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                </div>

                {/* 타이틀 */}
                <div className="px-4 pt-2">
                    <h2 className={`font-semibold text-[15px] leading-[150%] tracking-[-0.005em] ${isDarkMode ? 'text-white' : 'text-[#333]'}`}>
                        {t('question.slideTitle', '실시간 질문하기')}
                    </h2>
                </div>

                {/* 탭 헤더 */}
                <div className="px-4 mt-4">
                    <div className={`flex items-center h-10 rounded-lg p-1 gap-1 ${isDarkMode ? 'bg-[#2a2d32]' : 'bg-[#F0F1F2]'}`}>
                        <button
                            onClick={() => setActiveTab('question')}
                            className={`flex-1 min-w-0 flex items-center justify-center text-sm font-medium rounded-md border-none transition-all duration-200 ${
                                activeTab === 'question'
                                    ? `h-8 ${isDarkMode
                                        ? 'bg-[#444] text-white shadow-[0px_1px_1px_0px_rgba(10,13,18,0.15)]'
                                        : 'bg-white text-black shadow-[0px_1px_1px_0px_rgba(10,13,18,0.15)]'}`
                                    : `h-7 bg-transparent ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`
                            }`}
                        >
                            {t('question.title')}
                        </button>
                        <button
                            onClick={() => setActiveTab('history')}
                            className={`flex-1 min-w-0 flex items-center justify-center gap-2 text-sm font-medium rounded-md border-none transition-all duration-200 ${
                                activeTab === 'history'
                                    ? `h-8 ${isDarkMode
                                        ? 'bg-[#444] text-white shadow-[0px_1px_1px_0px_rgba(10,13,18,0.15)]'
                                        : 'bg-white text-black shadow-[0px_1px_1px_0px_rgba(10,13,18,0.15)]'}`
                                    : `h-7 bg-transparent ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`
                            }`}
                        >
                            <span>{t('question.list')}</span>
                            {questionHistory.length > 0 && (
                                <span
                                    className={`text-[12px] font-medium flex items-center justify-center rounded min-w-[20px] h-[22px] px-1.5 border leading-[150%] tracking-[-0.005em] ${
                                        isDarkMode ? 'border-[#666] text-[#5BA8F5] bg-[#333]' : 'border-[#E0E3E5] text-[#1181F9] bg-white'
                                    }`}
                                >
                                    {questionHistory.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* 컨텐츠 */}
                <div className="px-4 py-3 flex-1 min-h-0 overflow-hidden">
                    {activeTab === 'question' ? (
                        <div>

                            {/* 컨트롤 라인: 닉네임 | 언어 */}
                            <div className="flex items-center gap-2 py-1">

                                {/* 닉네임 - 텍스트+아이콘 스타일 */}
                                <div className="flex-shrink-0">
                                    {isEditingNickname ? (
                                        <div className="flex items-center gap-1 max-w-[200px]">
                                            <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${isDarkMode ? 'bg-[#253348]' : 'bg-sky-100'} overflow-hidden`}>
                                                <img src={`/edu/${nicknameAnimal}.svg`} alt={nicknameAnimal} className="w-5 h-5 object-contain" />
                                            </div>
                                            <input
                                                ref={nicknameInputRef}
                                                type="text"
                                                value={nickname}
                                                onChange={handleNicknameChange}
                                                onBlur={handleNicknameEditClose}
                                                onKeyDown={handleNicknameKeyDown}
                                                maxLength={MAX_NICKNAME_LENGTH}
                                                className={`flex-1 min-w-0 bg-transparent outline-none border-b font-medium text-[13px] leading-[150%] tracking-[-0.005em] ${
                                                    isDarkMode
                                                        ? 'text-gray-300 border-gray-500 placeholder-gray-600'
                                                        : 'text-[#575E64] border-gray-400 placeholder-gray-400'
                                                }`}
                                            />
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    const { name, animal } = generateRandomNickname();
                                                    setNickname(name);
                                                    setNicknameAnimal(animal);
                                                }}
                                                className={`flex-shrink-0 transition-colors ${
                                                    isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                                                }`}
                                            >
                                                {isDarkMode ? <RefreshIconDark className="w-6 h-6" /> : <RefreshIconLight className="w-6 h-6" />}
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1">
                                            <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${isDarkMode ? 'bg-[#253348]' : 'bg-sky-100'} overflow-hidden`}>
                                                <img src={`/edu/${nicknameAnimal}.svg`} alt={nicknameAnimal} className="w-5 h-5 object-contain" />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleNicknameEditOpen}
                                                className={`flex-1 truncate text-left font-medium text-[13px] leading-[150%] tracking-[-0.005em] transition-colors ${
                                                    isDarkMode ? 'text-gray-300 hover:text-gray-200' : 'text-[#575E64] hover:text-[#3d4248]'
                                                }`}
                                            >
                                                {nickname}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const { name, animal } = generateRandomNickname();
                                                    setNickname(name);
                                                    setNicknameAnimal(animal);
                                                }}
                                                className={`flex-shrink-0 transition-colors ${
                                                    isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                                                }`}
                                            >
                                                {isDarkMode ? <RefreshIconDark className="w-6 h-6" /> : <RefreshIconLight className="w-6 h-6" />}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1" />

                                {/* 언어 선택 */}
                                <div className="relative flex-shrink-0">
                                    <button
                                        onClick={() => { setShowLangSelector(!showLangSelector); setIsEditingNickname(false); }}
                                        className={`flex items-center justify-between w-[140px] h-10 py-2.5 px-3.5 gap-2 rounded-lg border transition-colors ${
                                            isDarkMode ? 'bg-[#333] border-[#666] text-gray-300' : 'bg-white border-[#D3D6D9] text-[#333]'
                                        }`}
                                    >
                                        <span className="font-normal text-sm leading-[150%] tracking-[-0.005em]">{currentLang?.name}</span>
                                        {isDarkMode
                                            ? <ChevronDownIconDark className={`w-[18px] h-[18px] transition-transform ${showLangSelector ? 'rotate-180' : ''}`} />
                                            : <ChevronDownIconLight className={`w-[18px] h-[18px] transition-transform ${showLangSelector ? 'rotate-180' : ''}`} />
                                        }
                                    </button>

                                    {showLangSelector && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={() => setShowLangSelector(false)} />
                                            <div className={`absolute top-full right-0 mt-2 w-[200px] rounded-lg border z-50 shadow-[0px_4px_6px_-1px_rgba(10,13,18,0.1)] ${
                                                isDarkMode ? 'bg-[#333] border-[#666]' : 'bg-white border-[#D3D6D9]'
                                            }`}>
                                                <div className="p-2">
                                                    {questionLanguageOptions.map((option) => (
                                                        <button
                                                            key={option.code}
                                                            onClick={() => handleLanguageSelect(option.code)}
                                                            className={`w-full h-9 text-left flex items-center justify-between transition-colors rounded-md py-2.5 pr-2 pl-2.5 ${
                                                                selectedQuestionLang === option.code
                                                                    ? isDarkMode ? 'bg-[#444] text-white' : 'bg-[#F5F5F5] text-[#333]'
                                                                    : isDarkMode ? 'hover:bg-[#444] text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                <span className={`font-normal text-sm leading-[150%] tracking-[-0.005em] ${isDarkMode ? 'text-[#ccc]' : 'text-[#2C2F32]'}`}>{option.name}</span>
                                                                {option.code !== 'auto' && (
                                                                    <span className={`flex items-center justify-center font-normal min-w-[22px] h-[18px] px-1 rounded text-[11px] leading-[150%] tracking-[-0.005em] ${
                                                                        isDarkMode ? 'bg-[#444] text-[#aaa]' : 'bg-[#E0E3E5] text-[#2C2F32]'
                                                                    }`}>
                                                                        {option.code.toUpperCase()}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {selectedQuestionLang === option.code && (
                                                                <CheckIcon className="flex-shrink-0 w-4 h-4" />
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                            </div>

                            {/* textarea 박스 */}
                            <div>
                                <div className={`relative rounded-lg overflow-hidden transition-colors duration-200 border min-w-[320px] ${
                                    isError
                                        ? isDarkMode ? 'border-red-500/60 bg-[#3a1f1f]' : 'border-red-400 bg-red-50'
                                        : voiceStatus === 'listening'
                                            ? isDarkMode ? 'border-blue-500/60 bg-[#1f2f3a]' : 'border-blue-400 bg-blue-50'
                                            : isDarkMode ? 'border-[#666] bg-[#333]' : 'border-[#D3D6D9] bg-white'
                                }`}>
                                    <div className="relative">
                                        <textarea
                                            ref={textareaRef}
                                            value={questionText}
                                            onChange={(e) => { if (e.target.value.length <= MAX_QUESTION_LENGTH) setQuestionText(e.target.value); }}
                                            onKeyDown={handleKeyDown}
                                            placeholder={voiceStatus === 'listening' ? t('question.voice.recording') : t('question.placeholder')}
                                            rows={3}
                                            disabled={voiceStatus === 'listening' || voiceStatus === 'processing'}
                                            className={`w-full py-4 px-3 resize-none outline-none bg-transparent whitespace-pre-wrap text-[15px] leading-[150%] tracking-[-0.005em] transition-opacity ${
                                                voiceStatus === 'listening' || voiceStatus === 'processing'
                                                    ? isDarkMode ? 'text-gray-500 placeholder-gray-600 opacity-60' : 'text-gray-400 placeholder-[#C4C8CC] opacity-60'
                                                    : isDarkMode ? 'text-white placeholder-gray-400' : 'text-black placeholder-[#C4C8CC]'
                                            }`}
                                        />
                                    </div>

                                    {/* 마이크 + 글자수 */}
                                    <div className="flex items-end justify-between p-3">
                                        <div className="flex items-center gap-3">
                                            {isVoiceSupported && (
                                                <>
                                                    <button
                                                        onClick={voiceStatus === 'listening' ? stopVoiceRecognition : startVoiceRecognition}
                                                        disabled={voiceStatus === 'processing'}
                                                        className="flex-shrink-0 flex items-center justify-center transition-all duration-200"
                                                    >
                                                        {voiceStatus === 'listening' ? (
                                                            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500">
                                                                <div className="flex items-center gap-0.5">
                                                                    <div className="w-1 h-2.5 bg-white rounded-full animate-pulse" />
                                                                    <div className="w-1 h-3.5 bg-white rounded-full animate-pulse [animation-delay:100ms]" />
                                                                    <div className="w-1 h-2.5 bg-white rounded-full animate-pulse [animation-delay:200ms]" />
                                                                </div>
                                                            </div>
                                                        ) : voiceStatus === 'processing' ? (
                                                            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-400">
                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                            </div>
                                                        ) : (
                                                            isDarkMode ? <MicIconDark className="w-10 h-10" /> : <MicIconLight className="w-10 h-10" />
                                                        )}
                                                    </button>
                                                    {voiceStatus !== 'idle' && (
                                                        <div className={`text-xs flex items-center gap-1.5 ${
                                                            voiceStatus === 'listening' ? 'text-blue-500' : voiceStatus === 'error' ? 'text-red-500' : 'text-gray-500'
                                                        }`}>
                                                            {voiceStatus === 'listening' && (
                                                                <><div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" /><span>{t('question.voice.recording')}</span></>
                                                            )}
                                                            {voiceStatus === 'processing' && <span>{t('question.voice.processing')}</span>}
                                                            {voiceStatus === 'error' && <span>{t('question.voice.error')}</span>}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <span className={`font-normal text-xs leading-[150%] tracking-[-0.005em] ${
                                            questionText.length >= MAX_QUESTION_LENGTH ? 'text-[#F04438]' : isDarkMode ? 'text-[#999]' : 'text-[#899199]'
                                        }`}>
                                            {questionText.length}/{MAX_QUESTION_LENGTH}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* 보내기 버튼 */}
                            <button
                                onClick={handleSubmit}
                                disabled={!canSubmit}
                                className={`w-full flex items-center justify-center h-12 rounded-lg mt-4 mb-3 pr-[18px] pl-5 gap-1 transition-all duration-200 ${
                                    isSuccess ? 'bg-green-500 text-white'
                                        : isError ? 'bg-red-500 text-white'
                                            : canSubmit ? 'bg-[#0075F8] text-white'
                                                : isDarkMode ? 'bg-gray-600 text-gray-400 cursor-not-allowed' : 'bg-[#0075F8] opacity-40 text-white cursor-not-allowed'
                                }`}
                            >
                                <span className="font-medium text-white text-[15px] leading-[150%] tracking-[-0.005em]">
                                    {isSuccess ? t('question.submitted')
                                        : isLoading ? t('question.submitting')
                                            : isError ? t('question.failed')
                                                : t('question.send', '보내기')}
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2 h-full min-h-0 overflow-y-auto">

                            {questionHistory.length > 0 ? (
                                <div>
                                    {questionHistory.map((question, index) => {
                                        const isLast = index === questionHistory.length - 1;
                                        return (
                                            <div
                                                key={question.id}
                                                className="w-full text-left flex gap-3 py-3 px-1"
                                            >
                                                {/* 타임라인 아이콘 + 연결선 */}
                                                <div className="flex flex-col items-center shrink-0 w-8">
                                                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
                                                        isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-100'
                                                    }`}>
                                                        {question.type === 'voice' ? (
                                                            <svg className={`w-3.5 h-3.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                            </svg>
                                                        ) : (
                                                            <svg className={`w-3.5 h-3.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                    {/* 연결선: 마지막 아이템은 표시 안 함 */}
                                                    {!isLast && (
                                                        <div
                                                            className={`w-px flex-1 mt-1 min-h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                                                        />
                                                    )}
                                                </div>

                                                {/* 메타 + 본문 */}
                                                <div className="flex-1 min-w-0 pb-1">
                                                    {/* 메타 라인: 닉네임 · 시간 · 언어 뱃지 */}
                                                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                                                        {question.nickname && (
                                                            <span
                                                                className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                                               
                                                            >
                                                                {question.nickname}
                                                            </span>
                                                        )}
                                                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                            {formatTime(question.timestamp)}
                                                        </span>
                                                        <span
                                                            className={`text-xs px-1.5 py-0.5 rounded ${
                                                                isDarkMode ? 'bg-[#2a2f3e] text-gray-400' : 'bg-gray-100 text-gray-500'
                                                            }`}
                                                           
                                                        >
                                                            {getLanguageDisplayName(question.language)}
                                                        </span>
                                                    </div>
                                                    {/* 본문 */}
                                                    <p
                                                        className={`m-0 text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                                                    >
                                                        {question.text}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div
                                    className={`min-h-[280px] flex flex-col justify-center items-center text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                                >
                                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-100'}`}>
                                        <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className={`text-base font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                        {t('question.empty.title')}
                                    </h3>
                                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {t('question.empty.subtitle')}
                                    </p>
                                    <button
                                        onClick={() => setActiveTab('question')}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                            isDarkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                                        }`}
                                       
                                    >
                                        {t('question.empty.button')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default LectureQuestionSlideUp;
